// src/components/Seguridad/Components/Tabs/Empleados/UnidadesOrgEmpleadoTab.jsx
import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Seguridad/Utils/Endpoints';
import { Models } from '../../../Seguridad/Utils/ModelsSeguridad';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../utils/SessionUser';

const TITLETAB = 'Asignación de Unidades Organizacionales';

// Endpoints
const endpointAssigned = ENDPOINTS.unidadesOrgEmpleado;
const endpointAssignedById = ENDPOINTS.unidadesOrgEmpleadoById;
const endpointAvailable = (tipo) => ENDPOINTS.unidadesOrgSecurity(tipo);

// Models
const normalizeAssigned = Models.unidadOrgEmpleado;
const normalizeAvailable = Models.unidadesOrg;

// Keys
const ID_ASSIGNED_KEY = 'idUnidadOrgEmpleado';
const ID_ITEM_KEY = 'idUnidadOrganizacional';
const FK_USER_KEY = 'idEmpleado';
const ITEM_NAME_KEY = 'nombre';

export default function UnidadesOrgEmpleadoTab({ fkValue, onClose, onDirtyChange, readOnly = false, onSaveSuccess }) {
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  // State for initial data (to calculate changes)
  const [initialAssigned, setInitialAssigned] = useState([]);

  // State for current local changes
  const [currentAssigned, setCurrentAssigned] = useState([]);

  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showChildData, setShowChildData] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const finalFilter = showChildData ? 'hijas' : 'propias';
      const [resAvailable, resAssigned, resEmployee] = await Promise.all([
        axios.get(endpointAvailable(finalFilter)),
        axios.get(endpointAssigned),
        axios.get(`${ENDPOINTS.empleados}/${fkValue}`),
      ]);

      let items = (resAvailable?.data || []).map(normalizeAvailable);
      const rawAssigned = (resAssigned?.data || []).map(normalizeAssigned);
      const empData = resEmployee.data;

      // Filter for current employee
      const myAssigned = rawAssigned.filter(r => Number(r[FK_USER_KEY]) === Number(fkValue));

      // Fetch missing items metadata if we are viewing assignments that we don't have in our security context
      const missingIds = myAssigned
        .map(r => r[ID_ITEM_KEY])
        .filter(id => !items.find(i => i[ID_ITEM_KEY] === id));

      if (missingIds.length > 0) {
        const fetchPromises = missingIds.map(id => {
          const baseUrl = endpointAvailable('propias').split('?')[0].replace(/\/security$/i, '');
          return axios.get(`${baseUrl}/${id}`).catch(() => null);
        });
        const missingRes = await Promise.all(fetchPromises);
        const missingItems = missingRes.filter(res => res && res.data).map(res => normalizeAvailable(res.data));
        items = [...items, ...missingItems];
      }

      setAllItems(items);
      setEmployeeData(empData);
      setInitialAssigned(myAssigned);
      setCurrentAssigned(myAssigned);
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: `Error al cargar ${TITLETAB}`, variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fkValue, showChildData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fkValue) {
      load();
    }
  }, [fkValue, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate available items (All Items - Current Assigned Items)
  const availableItems = useMemo(() => {
    const assignedIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
    const isSuperAdmin = isSessionSuperAdmin();
    const sessionEmpresaId = getSessionEmpresaId();

    let result = allItems.filter(r => {
      // 1. No estar asignado ya
      if (assignedIds.has(r[ID_ITEM_KEY])) return false;

      // 2. Aislamiento Multi-Tenant
      if (isSuperAdmin) return true;

      const idEmpresa = r.idEmpresa;
      const isOwn = !idEmpresa || Number(idEmpresa) === sessionEmpresaId;
      if (isOwn) return true;

      // Es de una empresa hija: solo se ve si el botón de "Ver Hijas" está activo
      return showChildData;
    });

    // 3. Ordenamiento Jerárquico
    return result.sort((a, b) => {
      const aIsOwn = !a.idEmpresa || Number(a.idEmpresa) === sessionEmpresaId;
      const bIsOwn = !b.idEmpresa || Number(b.idEmpresa) === sessionEmpresaId;
      if (aIsOwn && !bIsOwn) return -1;
      if (!aIsOwn && bIsOwn) return 1;

      // Por nombre de empresa
      const aNomEmp = a.nombreEmpresa || '';
      const bNomEmp = b.nombreEmpresa || '';
      const empComp = aNomEmp.localeCompare(bNomEmp);
      if (empComp !== 0) return empComp;

      // Por nombre del registro
      const aNom = a.nombre || '';
      const bNom = b.nombre || '';
      return aNom.localeCompare(bNom);
    });
  }, [allItems, currentAssigned, showChildData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enriched assigned items (add name from catalog)
  const enrichedAssigned = useMemo(() => {
    return currentAssigned.map(assigned => {
      const itemDef = allItems.find(i => i[ID_ITEM_KEY] === assigned[ID_ITEM_KEY]);
      const baseName = itemDef ? (itemDef[ITEM_NAME_KEY] || itemDef.nombre) : `Item #${assigned[ID_ITEM_KEY]}`;
      const empName = itemDef?.nombreEmpresa;
      return {
        ...assigned,
        _displayName: `(${empName || 'General'}): ${baseName}`,
      };
    });
  }, [currentAssigned, allItems]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (initialAssigned.length !== currentAssigned.length) return true;

    const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
    const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));

    for (let id of currentIds) {
      if (!initialIds.has(id)) return true;
    }
    return false;
  }, [initialAssigned, currentAssigned]);

  // Propagar estado "sucio"
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(hasChanges);
    }
  }, [hasChanges, onDirtyChange]);

  // --- Drag and Drop Handlers ---

  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);

    if (source === targetColumn) return;

    if (targetColumn === 'assigned' && source === 'available') {
      addLocal(item);
    } else if (targetColumn === 'available' && source === 'assigned') {
      removeLocal(item);
    }
  };

  // --- Local State Modifiers ---

  const addLocal = (item) => {
    const exists = currentAssigned.some(r => r[ID_ITEM_KEY] === item[ID_ITEM_KEY]);
    if (exists) return;

    const newAssignment = {
      [ID_ASSIGNED_KEY]: 0, // Temporary ID
      [FK_USER_KEY]: Number(fkValue),
      [ID_ITEM_KEY]: item[ID_ITEM_KEY],
    };

    setCurrentAssigned(prev => [...prev, newAssignment]);
  };

  const removeLocal = (assignedItem) => {
    setCurrentAssigned(prev => prev.filter(r => r[ID_ITEM_KEY] !== assignedItem[ID_ITEM_KEY]));
  };

  // --- Save / Cancel ---

  const handleCancel = () => {
    onClose?.();
    setCurrentAssigned(initialAssigned);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Identificar qué añadir y qué quitar
      const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
      const toAdd = currentAssigned.filter(r => !initialIds.has(r[ID_ITEM_KEY]));

      const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
      const toRemove = initialAssigned.filter(r => !currentIds.has(r[ID_ITEM_KEY]));

      // 2. VALIDACIÓN: Si se añade a una UO de empresa hija, el empleado DEBE tener usuario
      const sessionEmpresaId = getSessionEmpresaId();

      const itemsAddedToHija = toAdd.filter(r => {
        const def = allItems.find(i => i[ID_ITEM_KEY] === r[ID_ITEM_KEY]);
        return def?.idEmpresa 
            && Number(def.idEmpresa) !== Number(sessionEmpresaId)
            && def.tieneEmpleados === false; // Solo validar si la empresa destino no tiene ningún empleado aún
      });

      if (itemsAddedToHija.length > 0) {
        const nombre = employeeData?.nombre ?? employeeData?.Nombre ?? '';
        const apellido = employeeData?.apellidoPaterno ?? employeeData?.ApellidoPaterno ?? '';
        const empName = `${nombre} ${apellido}`.trim();
        const resUser = await axios.get(ENDPOINTS.usuarios);
        const idPersona = employeeData?.idPersona ?? employeeData?.IdPersona;
        const targetUser = (resUser.data || []).find(u => Number(u.idPersona ?? u.IdPersona) === Number(idPersona));
        const hasUser = !!targetUser;

        if (!hasUser) {
          setAlertDialog({
            open: true,
            title: '⚠️ Asignación Rechazada',
            message: `Para asignar "${empName}" a una empresa hija (delegación), primero debes crear un usuario para este empleado.`,
            variant: 'warning'
          });
          setSaving(false);
          return;
        }

        const idUsuario = targetUser.idUsuario ?? targetUser.IdUsuario;
        const [resUserRoles, resRoles] = await Promise.all([
          axios.get(ENDPOINTS.usuarioRolByUsuarioId(idUsuario)),
          axios.get(ENDPOINTS.roles)
        ]);

        console.log("--- DEBUG ADMIN VALIDATION ---");
        console.log("targetUser:", targetUser);
        console.log("idUsuario:", idUsuario);
        console.log("resUserRoles.data:", resUserRoles.data);

        const userRoles = (resUserRoles.data || []).map(Models.usuarioRol);
        const allRoles = (resRoles.data || []).map(Models.roles);

        console.log("Normalized userRoles:", userRoles);

        const hasAdminRole = userRoles.some(ur => {
          const roleDef = allRoles.find(r => Number(r.idRol) === Number(ur.idRol));
          console.log(`Found roleDef for ${ur.idRol}:`, roleDef?.nombre);
          const isMatch = roleDef && roleDef.nombre && roleDef.nombre.toLowerCase().trim() === 'administrador';
          console.log(`Is Administrador match? ${isMatch}`);
          return isMatch;
        });

        console.log("Result hasAdminRole:", hasAdminRole);
        console.log("------------------------------");

        if (!hasAdminRole) {
          setAlertDialog({
            open: true,
            title: '⚠️ Asignación Rechazada',
            message: `El usuario de "${empName}" debe tener asignado el rol de "Administrador" para ser delegado a una empresa hija.`,
            variant: 'warning'
          });
          setSaving(false);
          return;
        }
      }

      // 3. Ejecutar altas y bajas de UOs
      const promises = [];
      for (const item of toAdd) {
        promises.push(axios.post(endpointAssigned, {
          [ID_ASSIGNED_KEY]: 0,
          [FK_USER_KEY]: Number(fkValue),
          [ID_ITEM_KEY]: item[ID_ITEM_KEY],
        }));
      }

      for (const item of toRemove) {
        const idToDelete = item[ID_ASSIGNED_KEY];
        if (idToDelete > 0) {
          const url = typeof endpointAssignedById === 'function'
            ? endpointAssignedById(idToDelete)
            : `${endpointAssignedById}/${idToDelete}`;
          promises.push(axios.delete(url));
        }
      }

      await Promise.all(promises);

      // 4. Lógica inteligente de limpieza por empresa
      const empresasEliminadas = new Set(
        toRemove
          .map(r => {
            const def = allItems.find(i => i[ID_ITEM_KEY] === r[ID_ITEM_KEY]);
            return def?.idEmpresa;
          })
          .filter(id => id && id > 0)
      );

      const empresasRestantes = new Set(
        currentAssigned
          .map(r => {
            const def = allItems.find(i => i[ID_ITEM_KEY] === r[ID_ITEM_KEY]);
            return def?.idEmpresa;
          })
          .filter(id => id && id > 0)
      );

      const cleanupPromises = [];
      for (const idEmpresa of empresasEliminadas) {
        if (!empresasRestantes.has(idEmpresa)) {
          cleanupPromises.push(
            axios.post(ENDPOINTS.unidadesOrgLimpiarEmpresa, {
              idEmpleado: Number(fkValue),
              idEmpresa: Number(idEmpresa),
            })
          );
        }
      }

      if (cleanupPromises.length > 0) {
        await Promise.all(cleanupPromises);
      }

      await load();

      // 5. Mostrar mensaje de delegación SOLO si es la primera vez que se asigna
      //    a esa empresa hija (la UO tenía TieneEmpleadosUO = false antes de guardar)
      const isFirstDelegation = itemsAddedToHija.some(r => {
        const def = allItems.find(i => i[ID_ITEM_KEY] === r[ID_ITEM_KEY]);
        // TieneEmpleadosUO viene del catálogo de UOs antes de guardar
        return def && def.tieneEmpleadosUO === false;
      });

      if (isFirstDelegation) {
        const nombre = employeeData?.nombre ?? employeeData?.Nombre ?? '';
        const apellido = employeeData?.apellidoPaterno ?? employeeData?.ApellidoPaterno ?? '';
        const empName = `${nombre} ${apellido}`.trim();
        setAlertDialog({
          open: true,
          title: '🚀 Empresa Delegada',
          message: `¡Asignación exitosa! A partir de ahora la configuración de esta empresa corresponde a "${empName}". El modal se cerrará para aplicar los cambios.`,
          variant: 'success'
        });
        setTimeout(() => { onClose?.(); }, 3000);
        return;
      }

      setAlertDialog({ open: true, title: 'Éxito', message: 'Cambios guardados correctamente.', variant: 'success' });
      onSaveSuccess?.();

    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar los cambios.', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mini-crud">
      <div className="mini-topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            {readOnly 
              ? 'Modo de solo lectura. No se permiten cambios.' 
              : 'Arrastra o usa los botones (+ / x) para modificar. Guarda al finalizar.'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            className={`roles-btn ${showChildData ? 'is-active' : ''}`}
            onClick={() => {
              setShowChildData(!showChildData);
              // load se dispara por el useEffect al cambiar showChildData
            }}
            title={showChildData ? 'Ocultar unidades de empresas hijas' : 'Ver unidades de empresas hijas'}
            style={{
              padding: '6px 12px',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: showChildData ? '#e2e8f0' : '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {showChildData ? '👁️ Ocultar Hijas' : '👁️ Ver Hijas'}
          </button>
        </div>
      </div>

      <div className="dnd-container">
        {/* Left: Available */}
        {!readOnly && (
          <div
            className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, 'available')}
            onDrop={(e) => handleDrop(e, 'available')}
          >
            <div className="dnd-header">Disponibles (Origen)</div>
            {loading && availableItems.length === 0 ? (
              <div className="dnd-empty">Cargando...</div>
            ) : availableItems.length === 0 ? (
              <div className="dnd-empty">No hay elementos disponibles</div>
            ) : (
              availableItems.map(item => (
                <div
                  key={item[ID_ITEM_KEY]}
                  className="dnd-item"
                  draggable={!saving && !readOnly}
                  onDragStart={(e) => handleDragStart(e, item, 'available')}
                >
                  <span>{`(${item.nombreEmpresa || 'General'}): ${item[ITEM_NAME_KEY] || item.nombre || `Item #${item[ID_ITEM_KEY]}`}`}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => addLocal(item)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.2em',
                        color: '#28a745',
                        cursor: 'pointer',
                        padding: '0 5px'
                      }}
                      title="Agregar"
                    >
                      ＋
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Right: Assigned */}
        <div
          className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
          style={readOnly ? { width: '100%', border: '1px solid #ddd', background: '#f9f9f9' } : {}}
          onDragOver={(e) => handleDragOver(e, 'assigned')}
          onDrop={(e) => handleDrop(e, 'assigned')}
        >
          <div className="dnd-header">{readOnly ? 'Empresas / UOs Asignadas' : 'Asignados (Destino)'}</div>
          {loading && enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">{readOnly ? 'No hay unidades asignadas' : 'Arrastra aquí para asignar'}</div>
          ) : (
            enrichedAssigned.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                style={readOnly ? { filter: 'grayscale(1)', opacity: 0.8, cursor: 'default' } : {}}
                draggable={!saving && !readOnly}
                onDragStart={(e) => handleDragStart(e, item, 'assigned')}
              >
                <span>{item._displayName}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeLocal(item)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2em',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '0 5px'
                    }}
                    title="Remover"
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
          <button
            className="roles-btn"
            onClick={handleCancel}
            type="button"
          >
            Cancelar / Reestablecer
          </button>
          <button
            className="btn-action"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{
              opacity: (!hasChanges || saving) ? 0.5 : 1,
              cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer'
            }}
            type="button"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      )}

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message}
          variant={alertDialog.variant}
        />
      )}
    </div>
  );
}
